import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Sun } from "lucide-react";
import { api, COUNTRY_META } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ProductCard from "@/components/ProductCard";
import OliveLeaves from "@/components/OliveLeaves";

export default function CountryPage() {
  const { key } = useParams();
  const { lang, t, L } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/countries/${key}`).then((r) => setData(r.data)).catch(() => {});
  }, [key]);

  if (!data) return <div className="flex min-h-screen items-center justify-center pt-24 text-ink/50">...</div>;

  const d = data.detail || {};
  const hero = lang === "en" ? (d.hero_en || d.hero_ar) : d.hero_ar;
  const story = lang === "en" ? (d.story_en || d.story_ar) : d.story_ar;
  const seasons = (lang === "en" ? d.seasons_en : d.seasons_ar) || [];
  const isPS = data.key === "PS";

  return (
    <div className="min-h-screen">
      <section className="relative flex min-h-[60vh] items-center overflow-hidden pt-16">
        <img src={d.image} alt={hero} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-gradient absolute inset-0" />
        {isPS && <OliveLeaves count={10} />}
        <div className="relative mx-auto w-full max-w-7xl px-6">
          <div className="max-w-2xl">
            <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-ivory/30 bg-ivory/10 px-4 py-1.5 text-sm text-ivory backdrop-blur">
              <span className="text-lg">{COUNTRY_META[data.key]?.flag}</span> {L(data, "name")}
              {isPS && <span className="rounded-full bg-palred px-2 py-0.5 text-xs">{t("primary_identity")}</span>}
            </span>
            <h1 className="font-display text-4xl font-extrabold text-ivory sm:text-5xl">{hero}</h1>
            {story && <p className="mt-5 text-lg leading-relaxed text-ivory/85">{story}</p>}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-olive/10 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"><MapPin className="h-5 w-5 text-olive" /> {t("regions")}</h2>
            <div className="flex flex-wrap gap-2.5">
              {data.regions.length > 0 ? data.regions.map((r) => (
                <Link key={r.region} to={`/store?country=${data.key}&region=${encodeURIComponent(r.region)}`}
                  className="flex items-center gap-1.5 rounded-full border border-olive/20 bg-olive/5 px-4 py-2 text-sm font-medium text-olive-dark transition-colors hover:border-olive hover:bg-olive hover:text-ivory">
                  {r.region} <span className="text-ink/40">({r.count})</span>
                </Link>
              )) : <p className="text-ink/50">{t("no_regions")}</p>}
            </div>
          </div>
          <div className="rounded-3xl border border-olive/10 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"><Sun className="h-5 w-5 text-gold" /> {t("seasons")}</h2>
            <div className="space-y-2">
              {seasons.map((s, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl bg-olive/5 px-4 py-3">
                  <span className="font-medium text-ink">{s.name}</span>
                  <span className="text-sm text-ink/55">{s.when}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="mb-8 font-display text-2xl font-bold text-ink sm:text-3xl">{t("country_products")} {L(data, "name")}</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {data.products.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
